/**
 * Shared Receive-Delivery-Order form (PRD.211).
 *
 * Both `/inventory/purchase-orders/:purchaseOrderId/receive` and the drawer-
 * based receive flow on the Delivery Orders page render this form. The hook
 * owns all state + the SQL commit path so the stock-movement behavior stays
 * byte-equivalent across both entry points. The commit function is the ONLY
 * place that writes `stock_movements` — and only when `isReceive=true`.
 */

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import {
  qDeliveryOrdersByPurchaseOrder,
  qInventoryBatchAvailableQuantities,
  qInventoryBatchesByItem,
  qInventoryItemsList,
  qPurchaseOrderForReceive,
  qPurchaseOrderLinesByOrder,
  type DeliveryOrderRow,
  type InventoryBatchAvailabilityRow,
  type InventoryBatchRow,
  type InventoryItemRow,
  type PurchaseOrderLineRow,
  type PurchaseOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { useBrandTokens } from '../../shell/shared';

// ─── Draft types ───

export interface DOLineDraft {
  id: string;
  purchaseOrderLineId: string | null;
  itemId: string | null;
  itemName: string;
  itemSku: string | null;
  itemUnit: string | null;
  // PRD.264/265 — only batch-tracked items require a batch on receive.
  batchTracked: boolean;
  purchasingUnitOrdered: string | null;
  quantityOrdered: string;
  quantityReceived: string;
  bonusQuantityReceived: string;
  unitPrice: string;
  unitPriceWithoutBonus: string;
  rate: string;
  remarks: string;

  existingBatchId: string | null;
  existingBatchNumber: string | null;
  existingBatchExpiry: string | null;
  newBatch: NewBatchDraft | null;
}

export interface NewBatchDraft {
  batchNumber: string;
  expiryDate: string;
  receivedDate: string;
  remarks: string;
}

function poLineToDraft(poLine: PurchaseOrderLineRow): DOLineDraft {
  const qty = poLine.quantity_ordered ?? 0;
  return {
    id: poLine.id,
    purchaseOrderLineId: poLine.id,
    itemId: poLine.inventory_item_id,
    itemName: poLine.item_name_snapshot ?? '',
    itemSku: poLine.sku_snapshot,
    itemUnit: poLine.unit_snapshot,
    // Resolved from the inventory catalog after lines hydrate (see hook).
    batchTracked: false,
    purchasingUnitOrdered: poLine.unit_snapshot,
    quantityOrdered: String(qty),
    quantityReceived: String(qty),
    bonusQuantityReceived: String(poLine.bonus_quantity ?? 0),
    unitPrice: String(poLine.rate ?? 0),
    unitPriceWithoutBonus: String(poLine.rate ?? 0),
    rate: String(poLine.rate ?? 0),
    remarks: poLine.remarks ?? '',
    existingBatchId: null,
    existingBatchNumber: null,
    existingBatchExpiry: null,
    newBatch: null,
  };
}

function lineAmount(line: DOLineDraft): number {
  const qty = (Number(line.quantityReceived) || 0) + (Number(line.bonusQuantityReceived) || 0);
  const price = Number(line.unitPrice) || 0;
  const amount = qty * price;
  return Number.isFinite(amount) ? Math.max(0, amount) : 0;
}

// ─── Hook ───

export interface UseReceiveDeliveryOrderFormParams {
  purchaseOrderId: string;
  orgId: string;
  userId: string;
  userName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  /** Called after a successful draft or receive write. */
  onSaved: (kind: 'draft' | 'receive') => void;
}

export function useReceiveDeliveryOrderForm({
  purchaseOrderId,
  orgId,
  userId,
  userName,
  db,
  onSaved,
}: UseReceiveDeliveryOrderFormParams) {
  // Load PO header + lines + any prior DO drafts on this PO.
  const { data: poRows = [] } = usePowerSyncQuery<PurchaseOrderRow>(
    qPurchaseOrderForReceive(purchaseOrderId),
  );
  const po = poRows[0] ?? null;
  const { data: poLines = [] } = usePowerSyncQuery<PurchaseOrderLineRow>(
    qPurchaseOrderLinesByOrder(purchaseOrderId),
  );
  const { data: existingDos = [] } = usePowerSyncQuery<DeliveryOrderRow>(
    qDeliveryOrdersByPurchaseOrder(purchaseOrderId),
  );
  const alreadyReceived = useMemo(
    () => existingDos.find((d) => d.status === 'received') ?? null,
    [existingDos],
  );

  // PRD.264/265 — resolve which items are batch-tracked so non-batch items
  // (e.g. a Supply) don't force a batch on receive.
  const { data: allItems = [] } = usePowerSyncQuery<InventoryItemRow>(qInventoryItemsList());
  const batchTrackedById = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const it of allItems) m.set(it.id, it.batch_tracked === 1);
    return m;
  }, [allItems]);

  const today = new Date().toISOString().slice(0, 10);
  const [doNumber, setDoNumber] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState('');
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
  const [reference, setReference] = useState('');
  const [dateRaised, setDateRaised] = useState(today);
  const [deliveryDate, setDeliveryDate] = useState(today);
  const [receivedDate, setReceivedDate] = useState(today);
  const [amountsAre, setAmountsAre] = useState<'tax_exclusive' | 'tax_inclusive'>('tax_exclusive');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DOLineDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<null | 'draft' | 'receive'>(null);
  const [batchModalLineId, setBatchModalLineId] = useState<string | null>(null);

  // Hydrate lines from PO lines once they load. Resets when the PO changes.
  const [hydratedForPoId, setHydratedForPoId] = useState<string | null>(null);
  useEffect(() => {
    if (!purchaseOrderId) return;
    if (hydratedForPoId === purchaseOrderId) return;
    if (poLines.length === 0) return;
    setLines(
      poLines.map((pl) => {
        const draft = poLineToDraft(pl);
        return {
          ...draft,
          batchTracked: draft.itemId ? (batchTrackedById.get(draft.itemId) ?? false) : false,
        };
      }),
    );
    setHydratedForPoId(purchaseOrderId);
  }, [purchaseOrderId, poLines, hydratedForPoId, batchTrackedById]);

  // The inventory catalog can load after the lines hydrate; patch each line's
  // batch-tracked flag when the map resolves so gating stays correct.
  useEffect(() => {
    if (batchTrackedById.size === 0) return;
    setLines((prev) =>
      prev.map((l) =>
        l.itemId ? { ...l, batchTracked: batchTrackedById.get(l.itemId) ?? false } : l,
      ),
    );
  }, [batchTrackedById]);

  // Default supplier/DO number/amounts mode from PO when it loads.
  useEffect(() => {
    if (!supplierName && po?.supplier_name) setSupplierName(po.supplier_name);
    if (!supplierId && po?.supplier_id) setSupplierId(po.supplier_id);
    if (!doNumber && po?.po_number) {
      setDoNumber(`DO-${po.po_number}`);
    }
    if (po?.amounts_are === 'tax_exclusive' || po?.amounts_are === 'tax_inclusive') {
      setAmountsAre(po.amounts_are);
    }
  }, [po, supplierName, supplierId, doNumber]);

  function updateLine(id: string, patch: Partial<DOLineDraft>) {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }

  const subtotal = useMemo(() => lines.reduce((sum, l) => sum + lineAmount(l), 0), [lines]);

  const editingLine = useMemo(
    () => lines.find((l) => l.id === batchModalLineId) ?? null,
    [lines, batchModalLineId],
  );

  // ── Single SQL write path. `isReceive=true` writes stock_movements; ──
  // ── `isReceive=false` leaves the ledger untouched. This function is ──
  // ── the only place that touches stock_movements in the receive flow. ──
  async function commit(isReceive: boolean) {
    setError(null);
    if (!db) return setError('Local database not ready.');
    if (!orgId) return setError('Missing organization context.');
    if (!supplierName.trim()) return setError('Enter a supplier name.');
    if (lines.length === 0) return setError('Add at least one item.');

    if (isReceive) {
      if (!receivedDate) return setError('Received date is required to finalize.');
      for (const line of lines) {
        if (!line.itemId) return setError('Select an item for every line.');
        const qty = Number(line.quantityReceived);
        if (!Number.isFinite(qty) || qty <= 0)
          return setError('Enter a quantity received greater than zero on every line.');
        if (line.batchTracked && !line.existingBatchId && !line.newBatch)
          return setError(`Select or create a batch for ${line.itemName || 'this item'}.`);
        if (line.newBatch && !line.newBatch.batchNumber.trim())
          return setError('New batch number is required.');
      }
    }

    setSaving(isReceive ? 'receive' : 'draft');
    try {
      const doId = crypto.randomUUID();
      const now = new Date().toISOString();
      const number = doNumber.trim() || `DO-${now.slice(0, 10)}-${doId.slice(0, 4)}`;

      // 1) DO header
      await db.execute(
        `INSERT INTO delivery_orders (
          id, org_id, do_number, purchase_order_id,
          supplier_id, supplier_name, supplier_invoice_number, reference,
          date_raised, delivery_date, received_date,
          amounts_are, subtotal, invoice_total, notes,
          status, received_by, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          doId,
          orgId,
          number,
          purchaseOrderId || null,
          supplierId,
          supplierName.trim(),
          supplierInvoiceNumber.trim() || null,
          reference.trim() || null,
          dateRaised || null,
          deliveryDate || null,
          isReceive ? receivedDate : null,
          amountsAre,
          subtotal,
          subtotal,
          notes.trim() || null,
          isReceive ? 'received' : 'draft',
          isReceive ? userName || userId || null : null,
          now,
          now,
        ],
      );

      // 2) Lines (and optional new batches, and stock_movements when receiving)
      for (const line of lines) {
        let batchId = line.existingBatchId;
        let batchNumberSnapshot = line.existingBatchNumber;
        let batchExpirySnapshot = line.existingBatchExpiry;

        if (isReceive && !batchId && line.newBatch) {
          batchId = crypto.randomUUID();
          batchNumberSnapshot = line.newBatch.batchNumber.trim();
          batchExpirySnapshot = line.newBatch.expiryDate || null;
          await db.execute(
            `INSERT INTO inventory_batches (
              id, org_id, inventory_item_id, batch_number, expiry_date, received_date,
              status, remarks, created_at, updated_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              batchId,
              orgId,
              line.itemId,
              line.newBatch.batchNumber.trim(),
              line.newBatch.expiryDate || null,
              line.newBatch.receivedDate || receivedDate,
              'active',
              line.newBatch.remarks.trim() || null,
              now,
              now,
            ],
          );
        } else if (line.newBatch && !isReceive) {
          batchNumberSnapshot = line.newBatch.batchNumber.trim() || null;
          batchExpirySnapshot = line.newBatch.expiryDate || null;
        }

        const lineId = crypto.randomUUID();
        const qtyReceived = Number(line.quantityReceived) || 0;
        const bonus = Number(line.bonusQuantityReceived) || 0;
        const totalQtyIn = qtyReceived + bonus;
        const unitPrice = Number(line.unitPrice) || 0;
        const amount = totalQtyIn * unitPrice;

        await db.execute(
          `INSERT INTO delivery_order_lines (
            id, org_id, delivery_order_id, purchase_order_line_id,
            inventory_item_id, inventory_batch_id,
            item_name_snapshot, sku_snapshot, unit_snapshot, purchasing_unit_ordered,
            quantity_ordered, quantity_received, bonus_quantity_received,
            unit_price, unit_price_without_bonus, rate, amount,
            batch_number_snapshot, expiry_date_snapshot,
            remarks, created_at, updated_at
          ) VALUES (
            ?, ?, ?, ?,
            ?, ?,
            ?, ?, ?, ?,
            ?, ?, ?,
            ?, ?, ?, ?,
            ?, ?,
            ?, ?, ?
          )`,
          [
            lineId,
            orgId,
            doId,
            line.purchaseOrderLineId,
            line.itemId,
            batchId,
            line.itemName,
            line.itemSku,
            line.itemUnit,
            line.purchasingUnitOrdered,
            Number(line.quantityOrdered) || 0,
            qtyReceived,
            bonus,
            unitPrice,
            Number(line.unitPriceWithoutBonus) || 0,
            Number(line.rate) || 0,
            amount,
            batchNumberSnapshot,
            batchExpirySnapshot,
            line.remarks.trim() || null,
            now,
            now,
          ],
        );

        // 3) Stock movement — ONLY when receiving / finalizing.
        if (isReceive && line.itemId) {
          await db.execute(
            `INSERT INTO stock_movements (
              id, org_id, inventory_item_id, inventory_batch_id,
              source_type, source_id, source_line_id,
              movement_type, quantity_in, quantity_out, unit,
              movement_date, remarks, created_by, created_at, updated_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              orgId,
              line.itemId,
              batchId,
              'delivery_order',
              doId,
              lineId,
              'delivery_received',
              totalQtyIn,
              0,
              line.itemUnit,
              receivedDate,
              line.remarks.trim() || notes.trim() || null,
              userId || null,
              now,
              now,
            ],
          );
        }
      }

      onSaved(isReceive ? 'receive' : 'draft');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed. Please try again.';
      setError(msg);
      setSaving(null);
    }
  }

  return {
    // PO + DO state
    po,
    poLines,
    alreadyReceived,

    // form fields
    doNumber,
    setDoNumber,
    supplierName,
    setSupplierName,
    supplierInvoiceNumber,
    setSupplierInvoiceNumber,
    reference,
    setReference,
    dateRaised,
    setDateRaised,
    deliveryDate,
    setDeliveryDate,
    receivedDate,
    setReceivedDate,
    amountsAre,
    setAmountsAre,
    notes,
    setNotes,

    // lines
    lines,
    updateLine,
    subtotal,

    // batch modal
    batchModalLineId,
    setBatchModalLineId,
    editingLine,

    // status
    error,
    saving,

    // actions
    saveDraft: () => commit(false),
    receive: () => commit(true),
  };
}

export type ReceiveDeliveryOrderFormApi = ReturnType<typeof useReceiveDeliveryOrderForm>;

// ─── Body component (presentational) ───

export function ReceiveDeliveryOrderFormBody({ form }: { form: ReceiveDeliveryOrderFormApi }) {
  const fmt = useFormatMoney();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-5)',
      }}
    >
      {form.alreadyReceived && (
        <div
          role="alert"
          style={{
            background: 'var(--color-warning-bg, #fef3c7)',
            color: 'var(--color-warning-text, #92400e)',
            border: '1px solid #fcd34d',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-3) var(--sp-4)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          This Purchase Order has already been received in DO{' '}
          <strong>{form.alreadyReceived.do_number ?? form.alreadyReceived.id.slice(0, 8)}</strong>.
          Re-receiving is disabled to avoid duplicate stock movements.
        </div>
      )}
      {form.error && (
        <div
          role="alert"
          style={{
            background: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #fecaca',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-3) var(--sp-4)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {form.error}
        </div>
      )}

      <Card title="Delivery / Supplier Receiving">
        <Grid2>
          <Field label="DO No.">
            <TextInput
              value={form.doNumber}
              onChange={form.setDoNumber}
              placeholder="Auto-generated"
            />
          </Field>
          <Field label="Supplier" required>
            <TextInput value={form.supplierName} onChange={form.setSupplierName} />
          </Field>
        </Grid2>
        <Grid2>
          <Field label="Supplier Invoice No.">
            <TextInput
              value={form.supplierInvoiceNumber}
              onChange={form.setSupplierInvoiceNumber}
              placeholder="From supplier invoice"
            />
          </Field>
          <Field label="Reference">
            <TextInput
              value={form.reference}
              onChange={form.setReference}
              placeholder="Internal reference / DR no."
            />
          </Field>
        </Grid2>
        <Grid3>
          <Field label="Date Raised">
            <TextInput type="date" value={form.dateRaised} onChange={form.setDateRaised} />
          </Field>
          <Field label="Delivery Date">
            <TextInput type="date" value={form.deliveryDate} onChange={form.setDeliveryDate} />
          </Field>
          <Field label="Received Date" required>
            <TextInput type="date" value={form.receivedDate} onChange={form.setReceivedDate} />
          </Field>
        </Grid3>
        <Grid2>
          <Field label="Amounts Are">
            <select
              value={form.amountsAre}
              onChange={(e) =>
                form.setAmountsAre(e.target.value as 'tax_exclusive' | 'tax_inclusive')
              }
              style={{ ...inputStyle, height: 40, cursor: 'pointer' }}
            >
              <option value="tax_exclusive">Tax Exclusive</option>
              <option value="tax_inclusive">Tax Inclusive</option>
            </select>
          </Field>
          <Field label="Notes">
            <TextInput value={form.notes} onChange={form.setNotes} placeholder="Optional" />
          </Field>
        </Grid2>
      </Card>

      <Card title="Items Received">
        {form.lines.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-6)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Loading PO lines…
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
              <thead>
                <tr>
                  <Th>Item</Th>
                  <Th>Batch</Th>
                  <Th>Unit</Th>
                  <Th align="right">Qty Ord</Th>
                  <Th align="right">Qty Recv</Th>
                  <Th align="right">Bonus</Th>
                  <Th align="right">Unit Price</Th>
                  <Th align="right">Amount</Th>
                  <Th>Remarks</Th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line) => (
                  <DOLineRow
                    key={line.id}
                    line={line}
                    onChange={(patch) => form.updateLine(line.id, patch)}
                    onPickBatch={() => form.setBatchModalLineId(line.id)}
                  />
                ))}
                <tr>
                  <td colSpan={7} style={{ padding: '12px 8px', textAlign: 'right' }}>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      Invoice Total
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '12px 8px',
                      textAlign: 'right',
                      fontWeight: 'var(--font-weight-semi)',
                    }}
                  >
                    {fmt(form.subtotal)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {form.editingLine && (
        <BatchSelectorModal
          line={form.editingLine}
          defaultReceivedDate={form.receivedDate}
          onClose={() => form.setBatchModalLineId(null)}
          onApply={(patch) => {
            if (form.editingLine) {
              form.updateLine(form.editingLine.id, patch);
            }
            form.setBatchModalLineId(null);
          }}
        />
      )}
    </div>
  );
}

// ─── DO Line row ───

function DOLineRow({
  line,
  onChange,
  onPickBatch,
}: {
  line: DOLineDraft;
  onChange: (patch: Partial<DOLineDraft>) => void;
  onPickBatch: () => void;
}) {
  const fmt = useFormatMoney();
  const tdStyle: CSSProperties = {
    padding: '6px 8px',
    borderBottom: '1px solid var(--border)',
    verticalAlign: 'top',
  };
  const amount = lineAmount(line);
  const batchLabel = line.existingBatchNumber
    ? `Batch ${line.existingBatchNumber}`
    : line.newBatch?.batchNumber
      ? `New: ${line.newBatch.batchNumber}`
      : 'Select / create batch…';
  const batchBound = !!(line.existingBatchId || line.newBatch);
  return (
    <tr>
      <td style={{ ...tdStyle, minWidth: 200 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {line.itemName || '(Item)'}
        </div>
        {line.itemSku && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            SKU: {line.itemSku}
          </div>
        )}
      </td>
      <td style={{ ...tdStyle, minWidth: 180 }}>
        {line.itemId && !line.batchTracked ? (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            Not batch-tracked
          </span>
        ) : (
          <button
            type="button"
            onClick={onPickBatch}
            disabled={!line.itemId}
            style={{
              height: 32,
              padding: '0 8px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: batchBound ? 'var(--color-success-bg)' : 'var(--bg-surface)',
              color: batchBound ? 'var(--color-success-text)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              cursor: line.itemId ? 'pointer' : 'not-allowed',
              opacity: line.itemId ? 1 : 0.5,
              width: '100%',
              textAlign: 'left',
            }}
          >
            {batchLabel}
          </button>
        )}
      </td>
      <td style={tdStyle}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {line.itemUnit ?? '—'}
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {line.quantityOrdered}
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.quantityReceived}
          onChange={(e) => onChange({ quantityReceived: e.target.value })}
          style={{ ...inputStyle, textAlign: 'right' }}
        />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.bonusQuantityReceived}
          onChange={(e) => onChange({ bonusQuantityReceived: e.target.value })}
          style={{ ...inputStyle, textAlign: 'right' }}
        />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <input
          type="number"
          min="0"
          step="any"
          value={line.unitPrice}
          onChange={(e) => onChange({ unitPrice: e.target.value })}
          style={{ ...inputStyle, textAlign: 'right' }}
        />
      </td>
      <td
        style={{
          ...tdStyle,
          textAlign: 'right',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {fmt(amount)}
      </td>
      <td style={tdStyle}>
        <input
          type="text"
          value={line.remarks}
          onChange={(e) => onChange({ remarks: e.target.value })}
          placeholder="Optional"
          style={inputStyle}
        />
      </td>
    </tr>
  );
}

// ─── Batch Selector Modal ───

function BatchSelectorModal({
  line,
  defaultReceivedDate,
  onClose,
  onApply,
}: {
  line: DOLineDraft;
  defaultReceivedDate: string;
  onClose: () => void;
  onApply: (patch: Partial<DOLineDraft>) => void;
}) {
  const brandTokens = useBrandTokens();
  const itemId = line.itemId!;

  const { data: batchRows = [] } = usePowerSyncQuery<InventoryBatchRow>(
    qInventoryBatchesByItem(itemId),
  );
  const { data: availabilityRows = [] } = usePowerSyncQuery<InventoryBatchAvailabilityRow>(
    qInventoryBatchAvailableQuantities(itemId),
  );
  const availabilityMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of availabilityRows) m.set(a.inventory_batch_id, a.available_quantity ?? 0);
    return m;
  }, [availabilityRows]);

  const [creating, setCreating] = useState<NewBatchDraft | null>(
    line.newBatch ?? {
      batchNumber: '',
      expiryDate: '',
      receivedDate: defaultReceivedDate,
      remarks: '',
    },
  );

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Select or Create Batch"
      maxWidth={680}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginLeft: 'auto' }}>
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          {creating && (
            <Button
              variant="gradient"
              size="md"
              onClick={() => {
                if (!creating.batchNumber.trim()) return;
                onApply({
                  existingBatchId: null,
                  existingBatchNumber: null,
                  existingBatchExpiry: null,
                  newBatch: { ...creating, batchNumber: creating.batchNumber.trim() },
                });
              }}
            >
              Use New Batch
            </Button>
          )}
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-5) var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <section
          style={{
            padding: 'var(--sp-4)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface-2, #fafafa)',
          }}
        >
          <h3
            style={{
              margin: '0 0 var(--sp-3)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Receive into a new batch
          </h3>
          {creating && (
            <>
              <Grid2>
                <Field label="Batch Number" required>
                  <TextInput
                    value={creating.batchNumber}
                    onChange={(v) => setCreating({ ...creating, batchNumber: v })}
                    placeholder="e.g., LOT-2026-001"
                  />
                </Field>
                <Field label="Expiry Date">
                  <TextInput
                    type="date"
                    value={creating.expiryDate}
                    onChange={(v) => setCreating({ ...creating, expiryDate: v })}
                  />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="Received Date">
                  <TextInput
                    type="date"
                    value={creating.receivedDate}
                    onChange={(v) => setCreating({ ...creating, receivedDate: v })}
                  />
                </Field>
                <Field label="Remarks">
                  <TextInput
                    value={creating.remarks}
                    onChange={(v) => setCreating({ ...creating, remarks: v })}
                    placeholder="Optional"
                  />
                </Field>
              </Grid2>
            </>
          )}
        </section>

        {batchRows.length > 0 && (
          <section>
            <h3
              style={{
                margin: '0 0 var(--sp-3)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              …or add to an existing batch
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <Th>Batch Number</Th>
                  <Th>Expiry</Th>
                  <Th align="right">Available</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {batchRows.map((b) => {
                  const avail = availabilityMap.get(b.id) ?? 0;
                  return (
                    <tr key={b.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 6px', fontSize: 'var(--font-size-sm)' }}>
                        {b.batch_number ?? '—'}
                      </td>
                      <td
                        style={{
                          padding: '8px 6px',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {b.expiry_date ?? '—'}
                      </td>
                      <td
                        style={{
                          padding: '8px 6px',
                          textAlign: 'right',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        {avail}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                        <Button
                          variant="outline"
                          size="md"
                          onClick={() =>
                            onApply({
                              existingBatchId: b.id,
                              existingBatchNumber: b.batch_number,
                              existingBatchExpiry: b.expiry_date,
                              newBatch: null,
                            })
                          }
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </Modal>
  );
}

// ─── Small primitives ───

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

function Grid3({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 'var(--sp-4)',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        {label}
        {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const inputStyle: CSSProperties = {
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-family)',
};

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inputStyle, height: 40 }}
    />
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 8px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}
